var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2021, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useState, useRef, useEffect, forwardRef, } from 'react';
import { css } from '@emotion/react';
import Button from '../Button';
import styled from '../../styles/styled';
import { spacing } from '../../styles/style-mixins';
import Headline from '../Headline';
import Body from '../Body';
import Image from '../Image';
import CloseButton from '../CloseButton';
import { useAnimation } from '../../hooks/useAnimation';
import { applyMultipleRefs } from '../../util/refs';
const DEFAULT_HEIGHT = 'auto';
const bannerWrapperStyles = ({ theme, variant, }) => css `
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: relative;
  border-radius: ${theme.borderRadius.mega};
  background-color: ${variant === 'system'
    ? 'var(--cui-bg-accent)'
    : 'var(--cui-bg-subtle)'};
  overflow: hidden;
  transition: opacity 200ms ease-in-out, height 200ms ease-in-out,
    visibility 200ms ease-in-out;
`;
const NotificationBannerWrapper = styled('div')(bannerWrapperStyles);
const contentStyles = ({ theme }) => css `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: ${theme.spacings.giga};
  padding-right: ${theme.spacings.byte};
  max-width: 420px;
`;
const Content = styled('div')(contentStyles);
const headlineStyles = ({ theme }) => css `
  font-size: ${theme.typography.headline.four.fontSize};
  line-height: ${theme.typography.headline.four.lineHeight};

  ${theme.mq.mega} {
    font-size: ${theme.typography.headline.three.fontSize};
    line-height: ${theme.typography.headline.three.lineHeight};
  }
`;
const ResponsiveHeadline = styled(Headline)(headlineStyles, spacing({ bottom: 'byte' }));
const bodyStyles = ({ theme }) => css `
  font-size: ${theme.typography.body.two.fontSize};
  line-height: ${theme.typography.body.two.lineHeight};

  ${theme.mq.mega} {
    font-size: ${theme.typography.body.one.fontSize};
    line-height: ${theme.typography.body.one.lineHeight};
  }
`;
const ResponsiveBody = styled(Body)(bodyStyles, spacing({ bottom: 'byte' }));
const buttonStyles = ({ theme, size = 'giga' }) => css `
  padding-top: calc(${theme.spacings.bit} - ${theme.borderWidth.kilo});
  padding-bottom: calc(${theme.spacings.bit} - ${theme.borderWidth.kilo});
  ${theme.mq.mega} {
    padding-top: ${size === 'giga'
    ? `calc(${theme.spacings.kilo} - ${theme.borderWidth.kilo})`
    : `calc(${theme.spacings.bit} - ${theme.borderWidth.kilo})`};

    padding-bottom: ${size === 'giga'
    ? `calc(${theme.spacings.kilo} - ${theme.borderWidth.kilo})`
    : `calc(${theme.spacings.bit} - ${theme.borderWidth.kilo})`};
  }
`;
const ResponsiveButton = styled(Button)(buttonStyles);
const imageStyles = ({ theme, width, align, }) => css `
  border-radius: 0 ${theme.borderRadius.mega} ${theme.borderRadius.mega} 0;
  min-width: 0;
  width: ${width || 200}px;
  height: auto;
  object-fit: contain;
  object-position: ${align || 'center'};
`;
const StyledImage = styled(Image)(imageStyles);
const closeButtonStyles = ({ theme, notificationVariant, }) => css `
  position: absolute;
  top: ${theme.spacings.byte};
  right: ${theme.spacings.byte};
  background-color: ${notificationVariant === 'system'
    ? 'var(--cui-bg-accent)'
    : 'var(--cui-bg-subtle)'};
`;
const StyledCloseButton = styled(CloseButton)(closeButtonStyles);
/**
 * The NotificationBanner displays a notification with text, a call-to-action,
 * and optionally an image.
 */
export const NotificationBanner = forwardRef((_a, ref) => {
    var { headline, body, action, variant = 'system', image, onClose, closeButtonLabel, tracking, isVisible = true } = _a, props = __rest(_a, ["headline", "body", "action", "variant", "image", "onClose", "closeButtonLabel", "tracking", "isVisible"]);
    const contentElement = useRef(null);
    const [isOpen, setOpen] = useState(isVisible);
    const [height, setHeight] = useState(getHeight(contentElement));
    const [, setAnimating] = useAnimation();
    useEffect(() => {
        setAnimating({
            duration: 200,
            onStart: () => {
                setHeight(getHeight(contentElement));
                // Delaying the state update until the next animation frame ensures
                // that browsers render the new height before the animation starts.
                window.requestAnimationFrame(() => {
                    setOpen(isVisible);
                });
            },
            onEnd: () => {
                setHeight(DEFAULT_HEIGHT);
            },
        });
    }, [isVisible, setAnimating]);
    return (_jsxs(NotificationBannerWrapper, Object.assign({ ref: applyMultipleRefs(ref, contentElement), style: {
            opacity: isOpen ? 1 : 0,
            height: isOpen ? height : 0,
            visibility: isOpen ? 'visible' : 'hidden',
        }, variant: variant }, props, { children: [_jsxs(Content, { children: [_jsx(ResponsiveHeadline, Object.assign({ as: "h2" }, { children: headline })), _jsx(ResponsiveBody, { children: body }), _jsx(ResponsiveButton, Object.assign({}, action))] }), image && image.src && _jsx(StyledImage, Object.assign({}, image)), onClose && closeButtonLabel && (_jsx(StyledCloseButton, { notificationVariant: variant, label: closeButtonLabel, size: "kilo", onClick: onClose, tracking: tracking
                    ? Object.assign({ component: 'notification-close' }, tracking) : undefined }))] })));
});
export function getHeight(element) {
    if (!element || !element.current) {
        return DEFAULT_HEIGHT;
    }
    return `${element.current.scrollHeight}px`;
}
